import Modal from 'react-modal';
import styled from '@emotion/styled';
import { ClassNames, css } from '@emotion/react';
import { tokens, Button } from '@hotjar/hotsauce';
import type { ReactNode } from 'react';

export { MODAL_SIZES } from './modal-sizes';

export const ZINDEX = 900001;

const modalStyles = css`
  font-family: ${tokens.FONT_FAMILY}, sans-serif, Arial;
  position: relative;
  box-shadow: 0 15px 60px 4px rgba(0, 0, 0, 0.35);
  border-radius: 4px;
  background-color: ${tokens.color.ui_background};
  outline: 0;
  margin: 60px auto 30px auto;
  transition: transform 0.3s ease-out;
`;
const getOverlayStyles = (zIndex: number) => css`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  outline: 0;
  overflow-x: hidden;
  overflow-y: auto;
  background: ${tokens.color.ui_overlay};
  z-index: ${zIndex} !important;
  &.ReactModal__Overlay {
    opacity: 0;
    transition: opacity 200ms ease-in-out;
  }

  &.ReactModal__Overlay--after-open {
    opacity: 1;
  }

  &.ReactModal__Overlay--before-close {
    opacity: 0;
  }
`;

/**
 * @deprecated use hotsauce
 * [`<Modal.Header />`](https://storybook.hotjar.com/?path=/docs/components-modal--modal) instead
 * @see Modal.Header
 */
export const GenericModalTitle = styled.h2`
  margin: 0;
  font-weight: 500;
  font-size: 22px;
  padding: 0 0 25px;
  margin-bottom: 0 !important;
`;

/**
 * @deprecated use hotsauce
 * [`<Modal.Footer />`](https://storybook.hotjar.com/?path=/docs/components-modal--modal) instead
 * @see Modal.Footer
 */
export const GenericModalFooter = styled.div`
  background: ${tokens.color.ui_background};
  padding: 16px 30px 24px 30px;
  margin: 0;
  border-radius: 0 0 4px 4px;
  display: flex;
  justify-content: space-between;

  & button:last-child {
    margin-left: auto;
  }
`;

export interface CloseButtonProps {
  onClick: () => void;
}

export const CloseButton = ({ onClick }: CloseButtonProps): JSX.Element => (
  <button
    type="button"
    css={css`
      position: absolute;
      line-height: 36px;
      width: 40px;
      height: 40px;
      top: 10px;
      right: 10px;
      color: ${tokens.color.text_disabled};
      font: inherit;
      font-size: 32px;
      font-weight: 300;
      text-align: center;
      transition: all 0.3s ease-in-out;
      text-shadow: none;
      -webkit-appearance: none;
      padding: 0;
      cursor: pointer;
      background: transparent;
      border: 0;
      outline: none;
    `}
    onClick={onClick}
  >
    ×
  </button>
);

/**
 * @deprecated use hotsauce
 * [`<Modal.Body />`](https://storybook.hotjar.com/?path=/docs/components-modal--modal) instead
 * @see Modal.Body
 */
export const GenericModalContent = styled.div`
  padding: 30px;
`;

let isAppElementSet = false;

export type GenericModalProps = Modal.Props & {
  onRequestClose?: (() => void) | null;
  onSubmit?: (() => void) | null;
  children?: ReactNode;
  showCloseButton?: boolean;
  width?: number;
  zIndex?: number;
};

/**
 * @deprecated use hotsauce
 * [`<Modal />`](https://storybook.hotjar.com/?path=/docs/components-modal--modal) instead
 * @see Modal
 */
export const GenericModal = ({
  isOpen,
  onRequestClose,
  onSubmit,
  children,
  showCloseButton = true,
  width = 500,
  contentLabel = '',
  zIndex = ZINDEX,
  ...props
}: GenericModalProps): JSX.Element => {
  if (!isAppElementSet && document.querySelector('#site-container')) {
    Modal.setAppElement('#site-container');
    isAppElementSet = true;
  }

  return (
    <ClassNames>
      {({ css: classNamesCSS }) => (
        <Modal
          shouldCloseOnOverlayClick
          css={[modalStyles, `width: ${width}px;`]}
          overlayClassName={classNamesCSS(getOverlayStyles(zIndex))}
          onRequestClose={onRequestClose}
          isOpen={isOpen}
          contentLabel={contentLabel}
          /* FIXME: react/jsx-props-no-spreading */
          /* eslint-disable-next-line react/jsx-props-no-spreading */
          {...props}
        >
          {onRequestClose && showCloseButton && <CloseButton onClick={onRequestClose} />}
          {children}
          <div>{onSubmit && <Button onClick={onSubmit}>I agree</Button>}</div>
        </Modal>
      )}
    </ClassNames>
  );
};
